import { resetServerAcks } from "@app/storage";
import { getServices } from "../platform.js";
import { DEFAULT_SYNC_URL } from "./sync-server.js";

export const SYNC_URL_KEY = "sync_url";
export const SYNC_CURSOR_KEY = "sync_cursor";
/** DISPLAY ONLY — "last synced at" for the settings card. Nothing derives dirty
 *  state from it anymore: dirtiness is the per-row `mut > server_acked_mut` fact
 *  (see core sync/device-local.ts). */
export const SYNC_LAST_AT_KEY = "sync_last_at";
/** The `preferences_updated_at` clock value last acknowledged by the server —
 *  the preferences singleton's equivalent of `server_acked_mut`. Device-only. */
export const PREFERENCES_ACKED_AT_KEY = "preferences_server_acked_at";

export interface SyncMetadata {
  cursor: string | null;
  lastSyncAt: string | null;
}

export async function loadSyncUrl(): Promise<string> {
  return (await getServices().settings.get(SYNC_URL_KEY)) || DEFAULT_SYNC_URL;
}

export async function loadSyncMetadata(): Promise<SyncMetadata> {
  const settings = getServices().settings;
  return {
    cursor: (await settings.get(SYNC_CURSOR_KEY)) || null,
    lastSyncAt: (await settings.get(SYNC_LAST_AT_KEY)) || null,
  };
}

export async function saveSyncMetadata(cursor: string, lastSyncAt: string): Promise<void> {
  const settings = getServices().settings;
  await settings.set(SYNC_CURSOR_KEY, cursor);
  await settings.set(SYNC_LAST_AT_KEY, lastSyncAt);
}

/**
 * Forget this account's sync state: pull cursor, display timestamp, preference
 * ack — and every row's `server_acked_mut` (via {@link resetServerAcks}). The
 * acks describe what ONE account's server has been told; signing into a
 * different account with them intact would leave every acknowledged row
 * permanently invisible to its first push there. The cleared cursor forces the
 * full re-pull; the zeroed acks force the full re-push (the server's merge
 * no-ops identical rows, so re-pushing to the SAME account is harmless too).
 */
export async function clearSyncMetadata(): Promise<void> {
  const settings = getServices().settings;
  await settings.delete(SYNC_CURSOR_KEY);
  await settings.delete(SYNC_LAST_AT_KEY);
  await settings.delete(PREFERENCES_ACKED_AT_KEY);
  await resetServerAcks(getServices().db);
}
